import type { notifications as notificationContracts } from '@personal-blog/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiFetch, apiUrl, type ApiRequestError } from '@/lib/api';
import { refetchIntervalWhileHealthy } from '@/lib/query-policy';

export type NotificationFilter = notificationContracts.NotificationFilter;
export type Notification = notificationContracts.Notification;
export type NotificationPreference = notificationContracts.NotificationPreference;
export type NotificationPreferencePatch =
  notificationContracts.NotificationPreferencePatch;

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface PushSubscriptionResponse {
  subscription: {
    id: string;
    endpoint: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface PushUnsubscribeResponse {
  removed: boolean;
}

export interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
  tzid: string;
}

export interface QuietHoursResponse {
  quietHours: QuietHours;
}

export interface DigestPreviewItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  actionUrl: string;
  createdAt: string;
  cadence: string;
  scheduledFor: string;
  emailSubject: string;
  emailPreview: string;
  actionLabel: string;
}

export interface DigestPreviewResponse {
  generatedAt: string;
  items: DigestPreviewItem[];
}

export const NOTIFICATIONS_REFETCH_INTERVAL_MS = 30_000;

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: ['notifications', 'list'] as const,
  list: (filter: NotificationFilter, limit: number, offset: number) =>
    ['notifications', 'list', filter, limit, offset] as const,
  preferences: ['notifications', 'preferences'] as const,
  quietHours: ['notifications', 'quiet-hours'] as const,
  digestPreview: ['notifications', 'digest-preview'] as const,
};

export interface UseNotificationsOptions {
  filter?: NotificationFilter;
  limit?: number;
  offset?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
}

type NotificationPathOptions = Required<Pick<
  UseNotificationsOptions,
  'filter' | 'limit' | 'offset'
>>;

function notificationsPath(opts: NotificationPathOptions): string {
  const params = new URLSearchParams();
  if (opts.filter !== 'all') params.set('filter', opts.filter);
  params.set('limit', String(opts.limit));
  if (opts.offset > 0) params.set('offset', String(opts.offset));
  return `/api/notifications?${params.toString()}`;
}

export function useNotifications(
  opts: UseNotificationsOptions = {},
): UseQueryResult<notificationContracts.NotificationListResponse, ApiRequestError> {
  const filter = opts.filter ?? 'all';
  const limit = opts.limit ?? 30;
  const offset = opts.offset ?? 0;
  return useQuery<notificationContracts.NotificationListResponse, ApiRequestError>({
    queryKey: notificationKeys.list(filter, limit, offset),
    queryFn: () =>
      apiFetch<notificationContracts.NotificationListResponse>(
        notificationsPath({ filter, limit, offset }),
      ),
    enabled: opts.enabled ?? true,
    refetchInterval: refetchIntervalWhileHealthy(opts.refetchInterval ?? false),
    retry: false,
  });
}

function invalidateNotificationLists(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: notificationKeys.lists });
}

export function useNotificationsRealtime(enabled: boolean): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return undefined;
    }

    const source = new EventSource(apiUrl('/api/notifications/stream'), {
      withCredentials: true,
    });
    let fallbackTimer: number | null = null;
    const onInboxEvent = (): void => {
      invalidateNotificationLists(qc);
    };
    const startPollingFallback = (): void => {
      if (fallbackTimer !== null) return;
      source.close();
      onInboxEvent();
      fallbackTimer = window.setInterval(onInboxEvent, NOTIFICATIONS_REFETCH_INTERVAL_MS);
    };

    source.addEventListener('snapshot', onInboxEvent);
    source.addEventListener('notification', onInboxEvent);
    source.addEventListener('error', startPollingFallback);

    return () => {
      source.removeEventListener('snapshot', onInboxEvent);
      source.removeEventListener('notification', onInboxEvent);
      source.removeEventListener('error', startPollingFallback);
      source.close();
      if (fallbackTimer !== null) window.clearInterval(fallbackTimer);
    };
  }, [enabled, qc]);
}

export function useMarkNotificationRead(): UseMutationResult<
  notificationContracts.NotificationResponse,
  ApiRequestError,
  { id: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<notificationContracts.NotificationResponse>(
        `/api/notifications/${encodeURIComponent(id)}/read`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      invalidateNotificationLists(qc);
    },
  });
}

export function useMarkAllNotificationsRead(): UseMutationResult<
  notificationContracts.MarkAllReadResponse,
  ApiRequestError,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<notificationContracts.MarkAllReadResponse>(
        '/api/notifications/mark-all-read',
        { method: 'POST' },
      ),
    onSuccess: () => {
      invalidateNotificationLists(qc);
    },
  });
}

export function useDismissNotification(): UseMutationResult<undefined, ApiRequestError, { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<undefined>(`/api/notifications/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      invalidateNotificationLists(qc);
    },
  });
}

export interface UseNotificationPreferencesOptions {
  enabled?: boolean;
}

export function useNotificationPreferences(
  opts: UseNotificationPreferencesOptions = {},
): UseQueryResult<notificationContracts.NotificationPreferencesResponse, ApiRequestError> {
  return useQuery<notificationContracts.NotificationPreferencesResponse, ApiRequestError>({
    queryKey: notificationKeys.preferences,
    queryFn: () =>
      apiFetch<notificationContracts.NotificationPreferencesResponse>(
        '/api/notifications/preferences',
      ),
    enabled: opts.enabled ?? true,
    retry: false,
  });
}

export function useUpdateNotificationPreferences(): UseMutationResult<
  notificationContracts.NotificationPreferencesResponse,
  ApiRequestError,
  notificationContracts.PatchNotificationPreferencesRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<notificationContracts.NotificationPreferencesResponse>(
        '/api/notifications/preferences',
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      qc.setQueryData(notificationKeys.preferences, data);
    },
  });
}

export function useQuietHours(): UseQueryResult<QuietHoursResponse, ApiRequestError> {
  return useQuery<QuietHoursResponse, ApiRequestError>({
    queryKey: notificationKeys.quietHours,
    queryFn: () =>
      apiFetch<QuietHoursResponse>('/api/notifications/quiet-hours'),
    retry: false,
  });
}

export function useUpdateQuietHours(): UseMutationResult<
  QuietHoursResponse,
  ApiRequestError,
  Partial<QuietHours>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<QuietHoursResponse>('/api/notifications/quiet-hours', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(notificationKeys.quietHours, data);
    },
  });
}

export function useDigestPreview(): UseQueryResult<DigestPreviewResponse, ApiRequestError> {
  return useQuery<DigestPreviewResponse, ApiRequestError>({
    queryKey: notificationKeys.digestPreview,
    queryFn: () =>
      apiFetch<DigestPreviewResponse>('/api/notifications/digest-preview'),
    retry: false,
  });
}

export function useRegisterPushSubscription(): UseMutationResult<
  PushSubscriptionResponse,
  ApiRequestError,
  PushSubscriptionPayload
> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch<PushSubscriptionResponse>('/api/notifications/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useUnregisterPushSubscription(): UseMutationResult<
  PushUnsubscribeResponse,
  ApiRequestError,
  { endpoint: string }
> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch<PushUnsubscribeResponse>('/api/notifications/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
